import React from "react";
import { createPortal } from "react-dom";
import {
  BadgeEuro,
  CalendarDays,
  Check,
  CheckCircle2,
  Clipboard,
  ClipboardCheck,
  FileCheck2,
  FileText,
  Home,
  KeyRound,
  Landmark,
  Mail,
  MapPin,
  ShieldCheck,
  UserRound,
  UsersRound,
  Wallet,
  X,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./LenderManualLoanCaseReviewSave.css";

const REVIEW_SECTIONS = [
  {
    code: "PERSONAL",
    translationKey: "sections.personal",
    fallback: "Personal information",
  },
  {
    code: "FINANCIAL",
    translationKey: "sections.financial",
    fallback: "Financial information",
  },
  {
    code: "DOCUMENTS",
    translationKey: "sections.documents",
    fallback: "Documents",
  },
  {
    code: "PROPERTY",
    translationKey: "sections.property",
    fallback: "Property information",
  },
  {
    code: "FINAL_LOAN",
    translationKey: "sections.finalLoan",
    fallback: "Final loan details",
  },
];

const STEP_VALUE_KEYS = {
  PERSONAL: ["PERSONAL", "personal", "personal-info", "personalInfo"],
  FINANCIAL: ["FINANCIAL", "financial", "financial-info", "financialInfo"],
  DOCUMENTS: ["DOCUMENTS", "documents"],
  PROPERTY: ["PROPERTY", "property", "property-info", "propertyInfo"],
  FINAL_LOAN: [
    "FINAL_LOAN",
    "final-loan",
    "final-loan-details",
    "finalLoan",
    "finalLoanDetails",
  ],
};

const REQUIRED_MANUAL_DOCUMENT_COUNT = 3;

function isObject(value) {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function firstValue(...values) {
  return values.find(hasValue) ?? "";
}

function titleCase(value) {
  const text = String(value || "").trim();

  if (!text) {
    return "";
  }

  return text
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getStepValue(stepValues, stepCode) {
  const keys = STEP_VALUE_KEYS[stepCode] || [];

  for (const key of keys) {
    if (stepValues?.[key] !== undefined) {
      return stepValues[key];
    }
  }

  return null;
}

function unwrapSection(value, sectionKey) {
  if (!isObject(value)) {
    return value;
  }

  if (Object.prototype.hasOwnProperty.call(value, sectionKey)) {
    return value[sectionKey];
  }

  return value;
}

function findApplicantByRole(applicants, role) {
  if (!Array.isArray(applicants)) {
    return null;
  }

  return (
    applicants.find(
      (applicant) =>
        String(
          applicant?.applicant_role || applicant?.role || "",
        ).toUpperCase() === role,
    ) || null
  );
}

function getPrimaryApplicant(personalInfo, loanCaseData) {
  const applicants =
    personalInfo?.applicants ||
    loanCaseData?.personal_info?.applicants ||
    loanCaseData?.applicants ||
    [];

  return (
    personalInfo?.primary_applicant ||
    personalInfo?.applicant ||
    findApplicantByRole(applicants, "PRIMARY") ||
    loanCaseData?.personal_info?.primary_applicant ||
    loanCaseData?.primary_applicant ||
    (hasValue(personalInfo?.first_name) || hasValue(personalInfo?.last_name)
      ? personalInfo
      : null)
  );
}

function getCoApplicant(personalInfo, loanCaseData) {
  const applicants =
    personalInfo?.applicants ||
    loanCaseData?.personal_info?.applicants ||
    loanCaseData?.applicants ||
    [];

  return (
    personalInfo?.co_applicant ||
    findApplicantByRole(applicants, "CO_APPLICANT") ||
    loanCaseData?.personal_info?.co_applicant ||
    loanCaseData?.co_applicant ||
    null
  );
}

function getApplicantName(applicant) {
  return [applicant?.first_name, applicant?.middle_name, applicant?.last_name]
    .filter(hasValue)
    .join(" ")
    .trim();
}

function getPropertyAddress(propertyInfo) {
  if (!propertyInfo) {
    return "";
  }

  const existingAddress = firstValue(
    propertyInfo.full_address,
    propertyInfo.formatted_address,
    propertyInfo.address,
  );

  if (existingAddress) {
    return existingAddress;
  }

  const streetLine = [
    propertyInfo.street,
    propertyInfo.street_name,
    propertyInfo.house_number,
    propertyInfo.apartment_number ? `${propertyInfo.apartment_number}` : "",
  ]
    .filter(hasValue)
    .join(" ")
    .trim();

  const locationLine = [
    propertyInfo.postal_code,
    propertyInfo.city,
    propertyInfo.region,
    propertyInfo.country_code || propertyInfo.country,
  ]
    .filter(hasValue)
    .join(", ")
    .trim();

  return [streetLine, locationLine].filter(Boolean).join(", ");
}

function parseNumericValue(value) {
  if (!hasValue(value)) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let text = String(value)
    .trim()
    .replace(/[€%\s\u00A0\u202F]/g, "");

  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");

  if (lastComma !== -1 && lastDot !== -1) {
    const decimalSeparator = lastComma > lastDot ? "," : ".";
    const groupingSeparator = decimalSeparator === "," ? "." : ",";

    text = text
      .replace(new RegExp(`\\${groupingSeparator}`, "g"), "")
      .replace(decimalSeparator, ".");
  } else if (lastComma !== -1) {
    const decimalLength = text.length - lastComma - 1;

    text =
      decimalLength === 1 || decimalLength === 2
        ? text.replace(",", ".")
        : text.replace(/,/g, "");
  } else if (lastDot !== -1) {
    const decimalLength = text.length - lastDot - 1;

    if (decimalLength === 3 && text.indexOf(".") === lastDot) {
      text = text.replace(/\./g, "");
    }
  }

  text = text.replace(/[^\d.-]/g, "");

  const parsed = Number(text);

  return Number.isFinite(parsed) ? parsed : null;
}

function formatMoney(value, locale) {
  const number = parseNumericValue(value);

  if (number === null) {
    return "—";
  }

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number);
}

function formatPercent(value, locale) {
  const number = parseNumericValue(value);

  if (number === null) {
    return "—";
  }

  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(number)}%`;
}

function formatDateTime(value, locale) {
  if (!hasValue(value)) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function countUploadedDocuments(documentData, loanCaseData) {
  const directCount = [
    documentData?.uploaded_count,
    documentData?.uploaded_documents_count,
    loanCaseData?.documents_summary?.uploaded_count,
    loanCaseData?.documents?.uploaded_count,
    loanCaseData?.documents?.uploaded_documents_count,
    loanCaseData?.uploaded_documents_count,
  ].find((value) => Number.isFinite(Number(value)));

  if (directCount !== undefined) {
    return Number(directCount);
  }

  const possibleCollections = [
    Array.isArray(documentData) ? documentData : null,
    documentData?.uploaded_documents,
    documentData?.documents,
    documentData?.requirements,
    documentData?.items,
    loanCaseData?.documents?.uploaded_documents,
    Array.isArray(loanCaseData?.documents) ? loanCaseData.documents : null,
    loanCaseData?.document_requirements,
  ];

  const documents = possibleCollections.find(Array.isArray);

  if (!documents) {
    return 0;
  }

  return documents.filter((document) => {
    if (!document) {
      return false;
    }

    const status = String(document?.status || "").toUpperCase();

    return Boolean(
      document?.version_id ||
      document?.document_version_id ||
      document?.original_filename ||
      document?.file ||
      document?.uploaded_file ||
      document?.active_version ||
      document?.latest_version ||
      document?.versions?.length ||
      document?.files?.length ||
      ["UPLOADED", "UNDER_REVIEW", "RESUBMITTED", "COMPLETED"].includes(status),
    );
  }).length;
}

function getFundingSources(finalLoanDetails, rawFinalStep, loanCaseData) {
  if (Array.isArray(finalLoanDetails?.funding_sources)) {
    return finalLoanDetails.funding_sources;
  }

  if (Array.isArray(rawFinalStep?.funding_sources)) {
    return rawFinalStep.funding_sources;
  }

  if (Array.isArray(loanCaseData?.result?.funding_sources)) {
    return loanCaseData.result.funding_sources;
  }

  return [];
}

function isPropertyComplete(propertyInfo, marketRegion) {
  if (!isObject(propertyInfo)) {
    return false;
  }

  const requiredFields = [
    "property_type",
    "building_condition",
    "usable_area_sqm",
    "year_built",
    "epc_label",
    "epc_value",
    "heating_type",
    "cooling_type",
    "ownership_type",
    "street",
    "city",
    "postal_code",
  ];

  const hasMarketCountry = hasValue(
    marketRegion?.country_code ||
      propertyInfo?.country_code ||
      propertyInfo?.country,
  );

  return (
    hasMarketCountry &&
    requiredFields.every((fieldName) => hasValue(propertyInfo?.[fieldName]))
  );
}

function getRequiredFinalLoanFields(isBelgiumCase) {
  return [
    "approved_total_loan_amount",
    "approved_purchase_loan_amount",
    "approved_renovation_loan_amount",
    "property_price",
    "own_contribution_amount",
    "tenure_years",
    "indicative_apr_pct",
    "estimated_monthly_payment",
    ...(isBelgiumCase ? ["mortgage_amount", "mortgage_mandate_amount"] : []),
  ];
}

function isFinalLoanComplete(finalLoanDetails, isBelgiumCase) {
  if (!isObject(finalLoanDetails)) {
    return false;
  }

  return getRequiredFinalLoanFields(isBelgiumCase).every((fieldName) =>
    hasValue(finalLoanDetails?.[fieldName]),
  );
}

function getBackendStepCompletion(loanCaseData, stepCode) {
  const steps =
    loanCaseData?.manual_entry_steps ||
    loanCaseData?.steps ||
    loanCaseData?.loan_case?.manual_entry_steps ||
    [];

  if (!Array.isArray(steps)) {
    return null;
  }

  const step = steps.find(
    (candidate) =>
      String(candidate?.step_code || candidate?.code || "").toUpperCase() ===
      stepCode,
  );

  if (!step) {
    return null;
  }

  if (typeof step.is_completed === "boolean") {
    return step.is_completed;
  }

  if (typeof step.completed === "boolean") {
    return step.completed;
  }

  return null;
}

function getLocalStepValidity(stepValidity, stepCode) {
  const keys = STEP_VALUE_KEYS[stepCode] || [];

  for (const key of [stepCode, ...keys]) {
    const value = stepValidity?.[key];

    if (typeof value === "boolean") {
      return value;
    }

    if (isObject(value)) {
      if (typeof value.isValid === "boolean") {
        return value.isValid;
      }

      if (typeof value.is_valid === "boolean") {
        return value.is_valid;
      }
    }
  }

  return null;
}

async function copyText(value) {
  if (!value) {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement("textarea");

  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";

  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");

  document.body.removeChild(textarea);

  return copied;
}

function SummaryRow({
  icon: Icon,
  label,
  value,
  emphasized = false,
  multiline = false,
}) {
  return (
    <div
      className={`lender-review-save-step__summary-row ${
        emphasized ? "lender-review-save-step__summary-row--emphasized" : ""
      }`}
    >
      <span className="lender-review-save-step__summary-icon">
        <Icon size={16} strokeWidth={2.1} />
      </span>

      <span className="lender-review-save-step__summary-label">{label}</span>

      <strong
        className={`lender-review-save-step__summary-value ${
          multiline ? "lender-review-save-step__summary-value--multiline" : ""
        }`}
        title={value && value !== "—" ? String(value) : undefined}
      >
        {value || "—"}
      </strong>
    </div>
  );
}

function ReviewCard({
  icon: Icon,
  title,
  description,
  children,
  className = "",
}) {
  return (
    <section className={`lender-review-save-step__card ${className}`}>
      <header className="lender-review-save-step__card-header">
        <span className="lender-review-save-step__card-icon">
          <Icon size={19} strokeWidth={2.2} />
        </span>

        <div className="lender-review-save-step__card-heading">
          <h3>{title}</h3>

          {description ? <p>{description}</p> : null}
        </div>
      </header>

      <div className="lender-review-save-step__card-body">{children}</div>
    </section>
  );
}

function CompletionItem({ label, complete, t }) {
  return (
    <div
      className={`lender-review-save-step__completion-item ${
        complete
          ? "lender-review-save-step__completion-item--complete"
          : "lender-review-save-step__completion-item--incomplete"
      }`}
    >
      <span className="lender-review-save-step__completion-icon">
        {complete ? (
          <Check size={15} strokeWidth={2.8} />
        ) : (
          <span aria-hidden="true">!</span>
        )}
      </span>

      <span className="lender-review-save-step__completion-label">{label}</span>

      <strong>
        {complete
          ? t("states.complete", "Complete")
          : t("states.reviewRequired", "Review required")}
      </strong>
    </div>
  );
}

function SuccessModal({ t, locale, result, copiedField, onCopy, onClose }) {
  if (!result || typeof document === "undefined") {
    return null;
  }

  const invitation = result?.homebuyer_invitation || null;

  const caseReference = firstValue(
    result?.case_reference,
    result?.loan_case?.case_reference,
  );

  const dossierCode = firstValue(result?.dossier_code, result?.dossier?.code);

  const recordedAt = firstValue(
    result?.result?.recorded_at,
    result?.result_recorded_at,
    result?.manual_entry_completed_at,
  );

  const invitationReference = invitation?.token || "";

  const invitationEmail = invitation?.email || "";

  const invitationExpiresAt = invitation?.expires_at || "";

  const modal = (
    <div
      className="lender-review-save-step__success-backdrop"
      role="presentation"
    >
      <div
        className="lender-review-save-step__success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-case-success-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="lender-review-save-step__success-close"
          onClick={onClose}
          aria-label={t("success.close", "Close")}
        >
          <X size={18} />
        </button>

        <div className="lender-review-save-step__success-icon">
          <CheckCircle2 size={31} strokeWidth={2.15} />
        </div>

        <div className="lender-review-save-step__success-heading">
          <span>{t("success.eyebrow", "Manual loan case saved")}</span>

          <h2 id="manual-case-success-title">
            {t("success.title", "The loan case was finalized successfully")}
          </h2>

          <p>
            {t(
              "success.description",
              "The final lender result has been recorded and the homebuyer invitation has been created. You can copy the references below before returning to the loan-cases page.",
            )}
          </p>
        </div>

        <div className="lender-review-save-step__success-reference-grid">
          <div className="lender-review-save-step__success-reference lender-review-save-step__success-reference--primary">
            <span>
              {t("fields.platformCaseReference", "Loan case reference")}
            </span>

            <strong>{caseReference || "—"}</strong>

            {caseReference ? (
              <button
                type="button"
                onClick={() => onCopy("case", caseReference)}
              >
                {copiedField === "case" ? (
                  <>
                    <ClipboardCheck size={15} />
                    {t("buttons.copied", "Copied")}
                  </>
                ) : (
                  <>
                    <Clipboard size={15} />
                    {t("buttons.copy", "Copy")}
                  </>
                )}
              </button>
            ) : null}
          </div>

          <div className="lender-review-save-step__success-reference">
            <span>{t("fields.dossierReference", "Dossier reference")}</span>

            <strong>{dossierCode || "—"}</strong>

            {dossierCode ? (
              <button
                type="button"
                onClick={() => onCopy("dossier", dossierCode)}
              >
                {copiedField === "dossier" ? (
                  <>
                    <ClipboardCheck size={15} />
                    {t("buttons.copied", "Copied")}
                  </>
                ) : (
                  <>
                    <Clipboard size={15} />
                    {t("buttons.copy", "Copy")}
                  </>
                )}
              </button>
            ) : null}
          </div>

          <div className="lender-review-save-step__success-reference">
            <span>
              {t("fields.invitationReference", "Invitation reference")}
            </span>

            <strong className="lender-review-save-step__success-token">
              {invitationReference || "—"}
            </strong>

            {invitationReference ? (
              <button
                type="button"
                onClick={() => onCopy("invitation", invitationReference)}
              >
                {copiedField === "invitation" ? (
                  <>
                    <ClipboardCheck size={15} />
                    {t("buttons.copied", "Copied")}
                  </>
                ) : (
                  <>
                    <Clipboard size={15} />
                    {t("buttons.copy", "Copy")}
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>

        <div className="lender-review-save-step__success-meta">
          <span>
            <strong>
              {t("fields.invitationRecipient", "Invitation recipient")}
            </strong>

            {invitationEmail || "—"}
          </span>

          <span>
            <strong>{t("fields.recordedAt", "Recorded")}</strong>

            {formatDateTime(recordedAt, locale)}
          </span>

          <span>
            <strong>
              {t("fields.invitationExpires", "Invitation expires")}
            </strong>

            {formatDateTime(invitationExpiresAt, locale)}
          </span>
        </div>

        <div className="lender-review-save-step__success-note">
          <Mail size={17} />

          <span>
            {t(
              "success.invitationNote",
              "The invitation is also sent to the homebuyer by email. You can copy the references above before leaving this page.",
            )}
          </span>
        </div>

        <button
          type="button"
          className="lender-review-save-step__success-action"
          onClick={onClose}
        >
          {t("success.goToLoanCases", "Go to loan cases")}
        </button>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default function LenderManualLoanCaseReviewSave() {
  const { t, i18n } = useTranslation("lender-manual-review-save");

  const {
    loanCaseData,
    loanCaseId,
    currentStepKey,
    stepValues,
    stepValidity,
    handleStepValidityChange,

    finalizationResult = null,
    onFinalizationSuccessClose,
  } = useOutletContext();

  const [copiedField, setCopiedField] = React.useState("");

  const copyResetTimerRef = React.useRef(null);

  const locale = React.useMemo(() => {
    const language = String(i18n.resolvedLanguage || i18n.language || "en")
      .split("-")[0]
      .toLowerCase();

    return (
      {
        en: "en-GB",
        es: "es-ES",
        ca: "ca-ES",
        nl: "nl-BE",
      }[language] || "en-GB"
    );
  }, [i18n.language, i18n.resolvedLanguage]);

  const personalInfo = React.useMemo(
    () =>
      unwrapSection(getStepValue(stepValues, "PERSONAL"), "personal_info") ||
      loanCaseData?.personal_info ||
      null,
    [loanCaseData, stepValues],
  );

  const financialInfo = React.useMemo(
    () =>
      unwrapSection(getStepValue(stepValues, "FINANCIAL"), "financial_info") ||
      loanCaseData?.financial_info ||
      null,
    [loanCaseData, stepValues],
  );

  const documentData = React.useMemo(
    () =>
      unwrapSection(getStepValue(stepValues, "DOCUMENTS"), "documents") ||
      loanCaseData?.documents ||
      null,
    [loanCaseData, stepValues],
  );

  const propertyInfo = React.useMemo(
    () =>
      unwrapSection(getStepValue(stepValues, "PROPERTY"), "property_info") ||
      loanCaseData?.property_info ||
      loanCaseData?.dossier?.property_info ||
      loanCaseData?.property ||
      loanCaseData?.linked_property ||
      null,
    [loanCaseData, stepValues],
  );

  const rawFinalStep = React.useMemo(
    () => getStepValue(stepValues, "FINAL_LOAN"),
    [stepValues],
  );

  const finalLoanDetails = React.useMemo(
    () =>
      unwrapSection(rawFinalStep, "final_loan_details") ||
      loanCaseData?.final_loan_details ||
      loanCaseData?.result ||
      null,
    [loanCaseData, rawFinalStep],
  );

  const fundingSources = React.useMemo(
    () => getFundingSources(finalLoanDetails, rawFinalStep, loanCaseData),
    [finalLoanDetails, loanCaseData, rawFinalStep],
  );

  const primaryApplicant = React.useMemo(
    () => getPrimaryApplicant(personalInfo, loanCaseData),
    [loanCaseData, personalInfo],
  );

  const coApplicant = React.useMemo(
    () => getCoApplicant(personalInfo, loanCaseData),
    [loanCaseData, personalInfo],
  );

  const primaryApplicantName = getApplicantName(primaryApplicant);

  const coApplicantName = getApplicantName(coApplicant);

  const propertyAddress = getPropertyAddress(propertyInfo);

  const marketRegion =
    loanCaseData?.market_region || loanCaseData?.dossier?.market_region || null;

  const regionLabel = firstValue(
    marketRegion?.name,
    marketRegion?.code,
    loanCaseData?.market_region_name,
    propertyInfo?.region,
  );

  const countryCode = String(
    firstValue(
      marketRegion?.country_code,
      loanCaseData?.country_code,
      propertyInfo?.country_code,
      propertyInfo?.country,
    ) || "",
  )
    .trim()
    .toUpperCase();

  const isBelgiumCase = countryCode === "BE";

  const uploadedDocumentCount = countUploadedDocuments(
    documentData,
    loanCaseData,
  );

  const supplementaryFundingTotal = fundingSources.reduce(
    (total, source) => total + (parseNumericValue(source?.amount) || 0),
    0,
  );

  const renovationBudget = firstValue(
    financialInfo?.available_renovation_budget,
    loanCaseData?.financial_info?.available_renovation_budget,
    loanCaseData?.financing_details?.estimated_renovation_budget,
    loanCaseData?.final_loan_details?.estimated_renovation_budget,
  );

  const platformCaseReference = firstValue(
    loanCaseData?.case_reference,
    loanCaseData?.loan_case?.case_reference,
  );

  const lenderReference = firstValue(
    loanCaseData?.lender_external_reference,
    loanCaseData?.loan_case?.lender_external_reference,
  );

  const missingFinalLoanFields = React.useMemo(() => {
    if (!finalLoanDetails) {
      return [];
    }

    const labels = {
      approved_total_loan_amount: t(
        "fields.totalLoanAmount",
        "Total lender loan",
      ),
      approved_purchase_loan_amount: t(
        "fields.purchaseLoanAmount",
        "Purchase loan amount",
      ),
      approved_renovation_loan_amount: t(
        "fields.renovationLoanAmount",
        "Lender renovation loan",
      ),
      property_price: t("fields.propertyPrice", "Property purchase price"),
      own_contribution_amount: t(
        "fields.ownContributionAmount",
        "Own contribution",
      ),
      tenure_years: t("fields.tenure", "Tenure"),
      indicative_apr_pct: t("fields.indicativeApr", "Indicative APR"),
      estimated_monthly_payment: t(
        "fields.monthlyPayment",
        "Estimated monthly payment",
      ),
      mortgage_amount: t("fields.mortgageAmount", "Mortgage amount"),
      mortgage_mandate_amount: t(
        "fields.mortgageMandateAmount",
        "Mortgage mandate amount",
      ),
    };

    return getRequiredFinalLoanFields(isBelgiumCase)
      .filter((name) => !hasValue(finalLoanDetails?.[name]))
      .map((name) => ({
        name,
        label: labels[name] || name,
      }));
  }, [finalLoanDetails, isBelgiumCase, t]);

  const completionState = React.useMemo(() => {
    const computedCompletion = {
      PERSONAL: Boolean(primaryApplicantName),

      FINANCIAL: Boolean(
        financialInfo &&
        (hasValue(financialInfo.employment_status) ||
          hasValue(financialInfo.net_monthly_income) ||
          hasValue(financialInfo.estimated_renovation_budget) ||
          hasValue(financialInfo.requested_total_financing_amount) ||
          Object.keys(financialInfo).length > 0),
      ),

      DOCUMENTS: uploadedDocumentCount >= REQUIRED_MANUAL_DOCUMENT_COUNT,

      PROPERTY: isPropertyComplete(propertyInfo, marketRegion),

      FINAL_LOAN: isFinalLoanComplete(finalLoanDetails, isBelgiumCase),
    };

    return REVIEW_SECTIONS.reduce((result, section) => {
      const localValidity = getLocalStepValidity(stepValidity, section.code);

      const backendCompletion = getBackendStepCompletion(
        loanCaseData,
        section.code,
      );

      /*
       * A stale local false must not override data that is now
       * actually complete. This was the reason Property could
       * remain "Review required" after the form was completed.
       */
      result[section.code] = localValidity === false
        ? false
        : Boolean(
            computedCompletion[section.code] ||
            localValidity === true ||
            backendCompletion === true,
          );

      return result;
    }, {});
  }, [
    finalLoanDetails,
    financialInfo,
    isBelgiumCase,
    loanCaseData,
    marketRegion,
    primaryApplicantName,
    propertyInfo,
    stepValidity,
    uploadedDocumentCount,
  ]);

  const reviewReady = REVIEW_SECTIONS.every((section) =>
    Boolean(completionState[section.code]),
  );

  React.useEffect(() => {
    if (typeof handleStepValidityChange !== "function") {
      return;
    }

    handleStepValidityChange(
      currentStepKey,
      reviewReady,
      reviewReady ? [] : REVIEW_SECTIONS.filter(section => !completionState[section.code])
        .map(section => t(section.translationKey, section.fallback)),
    );
  }, [currentStepKey, handleStepValidityChange, loanCaseId, reviewReady]);

  React.useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        window.clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const handleCopySuccessValue = React.useCallback(async (field, value) => {
    if (!value) {
      return;
    }

    try {
      const copied = await copyText(value);

      if (!copied) {
        return;
      }

      setCopiedField(field);

      if (copyResetTimerRef.current) {
        window.clearTimeout(copyResetTimerRef.current);
      }

      copyResetTimerRef.current = window.setTimeout(() => {
        setCopiedField("");
      }, 1800);
    } catch (error) {
      console.error("Failed to copy finalized reference:", error);
    }
  }, []);

  return (
    <div className="lender-review-save-step">
      <section className="lender-review-save-step__notice">
        <span className="lender-review-save-step__notice-icon">
          <ShieldCheck size={22} strokeWidth={2.2} />
        </span>

        <div className="lender-review-save-step__notice-copy">
          <h2>{t("notice.title", "Review the case before saving it")}</h2>

          <p>
            {t(
              "notice.description",
              "Saving completes the lender-created loan case. The platform then generates a secure invitation reference that the homebuyer can use to connect their account and continue with OSS onboarding.",
            )}
          </p>
        </div>
      </section>

      <div className="lender-review-save-step__summary-grid">
        <ReviewCard
          icon={UserRound}
          title={t("cards.caseSummary.title", "Case summary")}
          description={t(
            "cards.caseSummary.description",
            "Confirm the applicant and property information.",
          )}
          className="lender-review-save-step__card--case"
        >
          <div className="lender-review-save-step__summary-list">
            <SummaryRow
              icon={UserRound}
              label={t("fields.primaryApplicant", "Primary applicant")}
              value={primaryApplicantName || "—"}
              emphasized
            />

            <SummaryRow
              icon={UsersRound}
              label={t("fields.coApplicant", "Co-applicant")}
              value={coApplicantName || t("states.notProvided", "Not provided")}
            />

            <SummaryRow
              icon={Mail}
              label={t("fields.homebuyerEmail", "Homebuyer email")}
              value={
                firstValue(
                  primaryApplicant?.email,
                  loanCaseData?.homebuyer?.email,
                ) || "—"
              }
            />

            <SummaryRow
              icon={Home}
              label={t("fields.propertyType", "Property type")}
              value={titleCase(propertyInfo?.property_type) || "—"}
            />

            <SummaryRow
              icon={MapPin}
              label={t("fields.propertyAddress", "Property address")}
              value={propertyAddress || "—"}
              multiline
            />

            <SummaryRow
              icon={Landmark}
              label={t("fields.region", "Market region")}
              value={regionLabel || "—"}
            />

            <SummaryRow
              icon={FileText}
              label={t("fields.uploadedDocuments", "Uploaded documents")}
              value={t("values.documentCount", {
                count: uploadedDocumentCount,
                defaultValue: "{{count}} documents",
              })}
            />
          </div>
        </ReviewCard>

        <ReviewCard
          icon={BadgeEuro}
          title={t("cards.finalLoan.title", "Final loan record")}
          description={t(
            "cards.finalLoan.description",
            "Confirm the approved financing and repayment terms.",
          )}
          className="lender-review-save-step__card--loan"
        >
          <div className="lender-review-save-step__summary-list">
            <SummaryRow
              icon={CheckCircle2}
              label={t("fields.outcome", "Outcome")}
              value={t("states.approved", "Approved")}
              emphasized
            />

            <SummaryRow
              icon={BadgeEuro}
              label={t("fields.totalLoanAmount", "Total loan amount")}
              value={formatMoney(
                finalLoanDetails?.approved_total_loan_amount,
                locale,
              )}
              emphasized
            />

            <SummaryRow
              icon={Home}
              label={t("fields.purchaseLoanAmount", "Purchase loan amount")}
              value={formatMoney(
                finalLoanDetails?.approved_purchase_loan_amount,
                locale,
              )}
            />

            <SummaryRow
              icon={FileCheck2}
              label={t("fields.renovationLoanAmount", "Renovation loan amount")}
              value={formatMoney(
                finalLoanDetails?.approved_renovation_loan_amount,
                locale,
              )}
            />

            <SummaryRow
              icon={BadgeEuro}
              label={t("fields.renovationBudget", "Renovation budget")}
              value={formatMoney(renovationBudget, locale)}
            />

            <SummaryRow
              icon={Wallet}
              label={t(
                "fields.supplementaryFunding",
                "Supplementary renovation funding",
              )}
              value={formatMoney(supplementaryFundingTotal, locale)}
            />

            <SummaryRow
              icon={CalendarDays}
              label={t("fields.tenure", "Tenure")}
              value={
                hasValue(finalLoanDetails?.tenure_years)
                  ? t("values.yearCount", {
                      count: Number(finalLoanDetails.tenure_years),
                      defaultValue: "{{count}} years",
                    })
                  : "—"
              }
            />

            <SummaryRow
              icon={Landmark}
              label={t("fields.indicativeApr", "Indicative APR")}
              value={formatPercent(
                finalLoanDetails?.indicative_apr_pct,
                locale,
              )}
            />

            <SummaryRow
              icon={BadgeEuro}
              label={t("fields.monthlyPayment", "Estimated monthly payment")}
              value={formatMoney(
                finalLoanDetails?.estimated_monthly_payment,
                locale,
              )}
            />

            {isBelgiumCase ? (
              <>
                <SummaryRow
                  icon={Landmark}
                  label={t("fields.mortgageAmount", "Mortgage amount")}
                  value={formatMoney(finalLoanDetails?.mortgage_amount, locale)}
                />

                <SummaryRow
                  icon={Landmark}
                  label={t(
                    "fields.mortgageMandateAmount",
                    "Mortgage mandate amount",
                  )}
                  value={formatMoney(
                    finalLoanDetails?.mortgage_mandate_amount,
                    locale,
                  )}
                />
              </>
            ) : null}
          </div>

          {fundingSources.length > 0 ? (
            <div className="lender-review-save-step__funding-list">
              <div className="lender-review-save-step__funding-heading">
                <strong>
                  {t("funding.title", "Supplementary funding sources")}
                </strong>

                <span>
                  {t(
                    "funding.description",
                    "These sources are separate from the lender renovation loan.",
                  )}
                </span>
              </div>

              {fundingSources.map((source, index) => (
                <div
                  key={
                    source?.id ||
                    source?.client_key ||
                    `${source?.name || "source"}-${index}`
                  }
                  className="lender-review-save-step__funding-item"
                >
                  <div>
                    <strong>
                      {source?.name || titleCase(source?.source_type)}
                    </strong>

                    <span>
                      {titleCase(source?.source_type)} ·{" "}
                      {titleCase(source?.status || "EXPECTED")}
                    </span>
                  </div>

                  <b>{formatMoney(source?.amount, locale)}</b>
                </div>
              ))}
            </div>
          ) : null}
        </ReviewCard>
      </div>

      <ReviewCard
        icon={KeyRound}
        title={t("cards.invitation.title", "Homebuyer invitation")}
        description={t(
          "cards.invitation.description",
          "The invitation is generated after the case is saved successfully.",
        )}
        className="lender-review-save-step__card--invitation lender-review-save-step__card--invitation-pending"
      >
        <div className="lender-review-save-step__invitation-content">
          <div className="lender-review-save-step__invitation-copy">
            <span className="lender-review-save-step__invitation-eyebrow">
              {t("invitation.pendingLabel", "Generated after saving")}
            </span>

            <h3>
              {t(
                "invitation.pendingTitle",
                "The platform will create the homebuyer invitation",
              )}
            </h3>

            <p>
              {t(
                "invitation.pendingDescription",
                "After a successful save, a confirmation window will show the loan-case reference, dossier reference and invitation reference before you return to the loan-cases page.",
              )}
            </p>
          </div>

          <div className="lender-review-save-step__reference-meta">
            <span>
              <strong>
                {t("fields.platformCaseReference", "Loan case reference")}
              </strong>

              {platformCaseReference || "—"}
            </span>

            <span>
              <strong>
                {t("fields.dossierReference", "Dossier reference")}
              </strong>

              {loanCaseData?.dossier_code || "—"}
            </span>

            <span>
              <strong>
                {t("fields.invitationRecipient", "Invitation recipient")}
              </strong>

              {firstValue(
                primaryApplicant?.email,
                loanCaseData?.homebuyer?.email,
              ) || "—"}
            </span>
          </div>
        </div>
      </ReviewCard>

      <ReviewCard
        icon={FileCheck2}
        title={t("cards.readiness.title", "Case readiness")}
        description={t(
          "cards.readiness.description",
          "Check the sections included in the final case before saving.",
        )}
        className="lender-review-save-step__card--readiness"
      >
        <div className="lender-review-save-step__completion-grid">
          {REVIEW_SECTIONS.map((section) => (
            <CompletionItem
              key={section.code}
              label={t(section.translationKey, section.fallback)}
              complete={Boolean(completionState[section.code])}
              t={t}
            />
          ))}
        </div>

        <div
          className={`lender-review-save-step__readiness-message ${
            reviewReady
              ? "lender-review-save-step__readiness-message--ready"
              : "lender-review-save-step__readiness-message--blocked"
          }`}
        >
          {reviewReady ? (
            <CheckCircle2 size={20} strokeWidth={2.3} />
          ) : (
            <ShieldCheck size={20} strokeWidth={2.3} />
          )}

          <div>
            <strong>
              {reviewReady
                ? t("readiness.readyTitle", "The case is ready to save")
                : t("readiness.blockedTitle", "Review the incomplete sections")}
            </strong>

            <span>
              {reviewReady
                ? t(
                    "readiness.readyDescription",
                    "Use the Save case action below. The backend will finalize the case and generate the invitation reference.",
                  )
                : missingFinalLoanFields.length > 0
                  ? t("readiness.missingFinalLoanFields", {
                      fields: missingFinalLoanFields
                        .map(({ label }) => label)
                        .join(", "),
                      defaultValue:
                        "Missing required final loan fields: {{fields}}.",
                    })
                  : t(
                      "readiness.blockedDescription",
                      "Return to the sections marked Review required and complete the missing information.",
                    )}
            </span>
          </div>
        </div>
      </ReviewCard>
      <SuccessModal
        t={t}
        locale={locale}
        result={finalizationResult}
        copiedField={copiedField}
        onCopy={handleCopySuccessValue}
        onClose={onFinalizationSuccessClose}
      />
    </div>
  );
}
